import { Check } from 'lucide-react';
import { ImageUpload } from '../ImageUpload';
import { BACKGROUND_COLORS, BACKGROUND_GRADIENTS, type BackgroundSettings as BgSettings } from '../../types/site';

interface BackgroundSettingsProps {
  value: BgSettings;
  onChange: (settings: BgSettings) => void;
}

const DEFAULT_BG: BgSettings = {
  type: 'none',
  color: null,
  gradient: null,
  imageUrl: null,
};

function parseGradientColors(gradient: string | null): { color1: string; color2: string } {
  if (!gradient) return { color1: '#FDE2D6', color2: '#F8E7EC' };
  const match = gradient.match(/#[A-Fa-f0-9]{6}/g);
  if (match && match.length >= 2) {
    return { color1: match[0], color2: match[1] };
  }
  return { color1: '#FDE2D6', color2: '#F8E7EC' };
}

function buildGradient(color1: string, color2: string): string {
  return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
}

export function BackgroundSettings({ value, onChange }: BackgroundSettingsProps) {
  const settings = { ...DEFAULT_BG, ...value };
  const gradientColors = parseGradientColors(settings.gradient);

  const handleTypeChange = (type: BgSettings['type']) => {
    onChange({ ...settings, type });
  };

  const handleColorSelect = (color: string) => {
    onChange({ ...settings, type: 'color', color });
  };

  const handleGradientSelect = (gradient: string) => {
    onChange({ ...settings, type: 'gradient', gradient });
  };

  const handleImageChange = (imageUrl: string) => {
    onChange({ ...settings, type: 'image', imageUrl: imageUrl || null });
  };

  const handleCustomColorChange = (color: string) => {
    onChange({ ...settings, type: 'color', color });
  };

  const handleGradientColor1Change = (color1: string) => {
    onChange({ ...settings, type: 'gradient', gradient: buildGradient(color1, gradientColors.color2) });
  };

  const handleGradientColor2Change = (color2: string) => {
    onChange({ ...settings, type: 'gradient', gradient: buildGradient(gradientColors.color1, color2) });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Hintergrund-Typ</label>
        <div className="grid grid-cols-4 gap-2">
          {[
            { type: 'none', label: 'Keiner' },
            { type: 'color', label: 'Farbe' },
            { type: 'gradient', label: 'Verlauf' },
            { type: 'image', label: 'Bild' },
          ].map(({ type, label }) => (
            <button
              key={type}
              type="button"
              onClick={() => handleTypeChange(type as BgSettings['type'])}
              className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                settings.type === type
                  ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-500 text-teal-700 dark:text-teal-400'
                  : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {settings.type === 'color' && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Farbe auswählen</label>
          <div className="flex flex-wrap gap-2">
            {BACKGROUND_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorSelect(color)}
                className={`w-10 h-10 rounded-lg border-2 transition-all flex items-center justify-center ${
                  settings.color === color ? 'border-teal-500 ring-2 ring-teal-200 dark:ring-teal-800' : 'border-gray-200 dark:border-slate-600'
                }`}
                style={{ backgroundColor: color }}
              >
                {settings.color === color && <Check className="w-4 h-4 text-teal-600" />}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 dark:text-slate-400">Eigene Farbe:</span>
            <input
              type="color"
              value={settings.color || '#FFFFFF'}
              onChange={(e) => handleCustomColorChange(e.target.value)}
              className="w-10 h-10 rounded-lg cursor-pointer border border-gray-200 dark:border-slate-600"
            />
          </div>
        </div>
      )}

      {settings.type === 'gradient' && (
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Verlauf auswählen</label>
          <div className="grid grid-cols-2 gap-2">
            {BACKGROUND_GRADIENTS.map((gradient, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleGradientSelect(gradient)}
                className={`h-14 rounded-lg border-2 transition-all flex items-center justify-center ${
                  settings.gradient === gradient ? 'border-teal-500 ring-2 ring-teal-200 dark:ring-teal-800' : 'border-gray-200 dark:border-slate-600'
                }`}
                style={{ background: gradient }}
              >
                {settings.gradient === gradient && (
                  <div className="w-6 h-6 rounded-full bg-white/80 flex items-center justify-center">
                    <Check className="w-4 h-4 text-teal-600" />
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">Eigenen Verlauf erstellen</label>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Farbe 1</label>
                <input
                  type="color"
                  value={gradientColors.color1}
                  onChange={(e) => handleGradientColor1Change(e.target.value)}
                  className="w-full h-10 rounded-lg cursor-pointer border border-gray-200 dark:border-slate-600"
                />
              </div>
              <div className="text-gray-300 dark:text-slate-500 pt-5">→</div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Farbe 2</label>
                <input
                  type="color"
                  value={gradientColors.color2}
                  onChange={(e) => handleGradientColor2Change(e.target.value)}
                  className="w-full h-10 rounded-lg cursor-pointer border border-gray-200 dark:border-slate-600"
                />
              </div>
            </div>
            <div
              className="mt-3 h-8 rounded-lg border border-gray-200 dark:border-slate-600"
              style={{ background: buildGradient(gradientColors.color1, gradientColors.color2) }}
            />
          </div>
        </div>
      )}

      {settings.type === 'image' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Hintergrundbild</label>
          <ImageUpload
            value={settings.imageUrl || ''}
            onChange={handleImageChange}
            aspectRatio="16:9"
            placeholder="Bild hochladen"
          />
        </div>
      )}
    </div>
  );
}

export function getBackgroundStyle(background?: BgSettings): React.CSSProperties {
  if (!background || background.type === 'none') return {};

  switch (background.type) {
    case 'color':
      return background.color ? { backgroundColor: background.color } : {};
    case 'gradient':
      return background.gradient ? { background: background.gradient } : {};
    case 'image':
      return background.imageUrl
        ? {
            backgroundImage: `url(${background.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : {};
    default:
      return {};
  }
}
